import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ACard, btnPrimary, inputDark } from "@/components/shared/AdminKit";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { ProfileFields, StatusBadge, Field } from "@/components/agents/AgentFields";
import { PermissionMatrix } from "@/components/agents/PermissionMatrix";
import { LanguagePicker } from "@/components/agents/LanguagePicker";
import { TempPasswordBox } from "@/components/agents/TempPasswordBox";
import { AgentActivity } from "@/components/agents/AgentActivity";
import { SupportPerformance } from "@/components/agents/SupportPerformance";
import { useAgentStatus } from "@/pages/admin/AdminAgents";
import { useTr, fmtDate } from "@/lib/tr";
import { ArrowLeft, Loader2, Save, ShieldOff } from "lucide-react";

const PROFILE_KEYS = ["first_name", "last_name", "email", "phone", "job_title", "department", "role_description", "start_date", "manager_id", "internal_notes"];

function ProfileTab({ a, managers, reload, tr }) {
  const [form, setForm] = useState(() => Object.fromEntries(PROFILE_KEYS.map((k) => [k, a[k] || ""])));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/agents/${a.id}`, { ...form, manager_id: form.manager_id || null, start_date: form.start_date || null });
      toast.success(data.email_reverification ? tr("Enregistré — l'agent devra confirmer sa nouvelle adresse à la prochaine connexion", "Saved — the agent must confirm the new address at next login") : tr("Profil enregistré", "Profile saved"));
      reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <ACard className="p-5">
      <ProfileFields tr={tr} form={form} set={(k, v) => setForm((f) => ({ ...f, [k]: v }))} managers={managers.filter((m) => m.id !== a.id)} />
      <div className="mt-5 flex justify-end"><button onClick={save} disabled={busy} className={btnPrimary} data-testid="agent-profile-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}{tr("Enregistrer", "Save")}</button></div>
    </ACard>
  );
}

function PermsTab({ a, catalog, canSensitive, reload, tr }) {
  const [perms, setPerms] = useState(a.permissions || []);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/admin/agents/${a.id}/permissions`, { permissions: perms }); toast.success(tr("Permissions enregistrées et appliquées", "Permissions saved and applied")); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const denied = (catalog?.groups || []).flatMap((g) => g.permissions).filter((p) => !perms.includes(p.key));
  return (
    <div className="space-y-4">
      <PermissionMatrix catalog={catalog} value={perms} onChange={setPerms} canSensitive={canSensitive} />
      <p className="text-xs text-slate-500" data-testid="agent-denied-count">{tr("Permissions refusées", "Denied permissions")} : {denied.length}</p>
      <div className="flex justify-end"><button onClick={save} disabled={busy} className={btnPrimary} data-testid="agent-perms-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{tr("Enregistrer les permissions", "Save permissions")}</button></div>
    </div>
  );
}

function LangTab({ a, catalog, reload, tr }) {
  const [mode, setMode] = useState(a.languages_mode || "selected");
  const [langs, setLangs] = useState(a.allowed_languages || []);
  const save = async () => {
    try {
      const { data } = await api.put(`/admin/agents/${a.id}/languages`, { mode, languages: langs });
      toast.success(tr("Langues enregistrées", "Languages saved") + (data.tickets_released ? ` — ${data.tickets_released} ${tr("ticket(s) remis en file", "ticket(s) returned to queue")}` : ""));
      reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <ACard className="space-y-4 p-5">
      <LanguagePicker languages={catalog?.languages} mode={mode} value={langs} onChange={(m, l) => { setMode(m); setLangs(l); }} />
      <p className="text-xs text-slate-500">{tr("Appliqué immédiatement côté serveur : liste, détails, réponses, pièces jointes, statuts et transferts.", "Enforced server-side immediately: list, details, replies, attachments, status and transfers.")}</p>
      <div className="flex justify-end"><button onClick={save} className={btnPrimary} data-testid="agent-langs-save">{tr("Enregistrer les langues", "Save languages")}</button></div>
    </ACard>
  );
}

function SecurityTab({ a, reload, tr }) {
  const [mode, setMode] = useState("generate");
  const [pw, setPw] = useState(""); const [pw2, setPw2] = useState(""); const [must, setMust] = useState(true);
  const [temp, setTemp] = useState(null);
  const onStatus = useAgentStatus(reload);
  const reset = async () => {
    try {
      const { data } = await api.post(`/admin/agents/${a.id}/reset-password`, { mode, password: pw, password_confirm: pw2, must_change_password: must });
      setTemp(data.temporary_password); setPw(""); setPw2("");
      toast.success(tr("Mot de passe réinitialisé — sessions révoquées", "Password reset — sessions revoked")); reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const revoke = async () => { try { await api.post(`/admin/agents/${a.id}/revoke-sessions`); toast.success(tr("Sessions révoquées", "Sessions revoked")); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <ACard title={tr("Réinitialiser le mot de passe", "Reset password")} className="[&>div:last-child]:p-5">
        <div className="space-y-3 p-5">
          <select value={mode} onChange={(e) => setMode(e.target.value)} className={inputDark} data-testid="reset-mode">
            <option value="generate">{tr("Générer un mot de passe temporaire", "Generate a temporary password")}</option>
            <option value="set">{tr("Définir un nouveau mot de passe", "Set a new password")}</option>
          </select>
          {mode === "set" && <>
            <Field label={tr("Nouveau mot de passe", "New password")}><input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className={inputDark} data-testid="reset-password" autoComplete="new-password" /></Field>
            <Field label={tr("Confirmation", "Confirm")}><input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className={inputDark} data-testid="reset-password-confirm" autoComplete="new-password" /></Field>
            <label className="flex items-center gap-2 text-sm text-slate-300"><Checkbox checked={must} onCheckedChange={(v) => setMust(!!v)} data-testid="reset-must-change" />{tr("Changement obligatoire à la prochaine connexion", "Must change at next login")}</label>
          </>}
          <button onClick={reset} className={btnPrimary} data-testid="reset-password-submit">{tr("Réinitialiser", "Reset")}</button>
          <TempPasswordBox password={temp} onClose={() => setTemp(null)} />
        </div>
      </ACard>
      <ACard title={tr("Accès au compte", "Account access")}>
        <div className="space-y-3 p-5 text-sm">
          <div className="flex items-center gap-2">{tr("Statut", "Status")} : <StatusBadge tr={tr} status={a.status} testid="agent-detail-status" /></div>
          <div className="flex flex-wrap gap-2">
            {a.status === "active" && <button onClick={() => onStatus(a, "suspended")} className="rounded-full border border-amber-600/50 px-4 py-2 text-amber-300 hover:bg-amber-500/10" data-testid="detail-suspend">{tr("Suspendre", "Suspend")}</button>}
            {a.status !== "active" && <button onClick={() => onStatus(a, "active")} className="rounded-full border border-emerald-600/50 px-4 py-2 text-emerald-300 hover:bg-emerald-500/10" data-testid="detail-reactivate">{tr("Réactiver", "Reactivate")}</button>}
            {a.status !== "disabled" && <button onClick={() => onStatus(a, "disabled")} className="rounded-full border border-red-600/50 px-4 py-2 text-red-300 hover:bg-red-500/10" data-testid="detail-disable">{tr("Désactiver", "Disable")}</button>}
            <button onClick={revoke} className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-4 py-2 hover:bg-slate-800" data-testid="detail-revoke"><ShieldOff className="h-4 w-4" />{tr("Révoquer les sessions", "Revoke sessions")}</button>
          </div>
          <p className="text-xs text-slate-500">{tr("Aucun mot de passe n'est stocké ni affiché en clair. Un mot de passe enregistré ne peut pas être récupéré.", "No password is stored or shown in clear text. A saved password cannot be retrieved.")}</p>
        </div>
      </ACard>
    </div>
  );
}

function Summary({ a, tr }) {
  const item = (l, v, id) => <div className="min-w-0"><div className="text-xs text-slate-500">{l}</div><div className="mt-0.5 break-all text-sm text-white" data-testid={id}>{v || "—"}</div></div>;
  return (
    <ACard className="mb-5 p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div><h1 className="font-display text-2xl font-bold text-white" data-testid="agent-detail-name">{a.name}</h1><div className="text-sm text-slate-400">{a.job_title}{a.department ? ` · ${a.department}` : ""}</div></div>
        <StatusBadge tr={tr} status={a.status} testid="agent-summary-status" />
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {item(tr("E-mail", "Email"), <>{a.email} {a.email_verified ? <span className="text-emerald-400">✓</span> : <span className="text-xs text-amber-300">({tr("non vérifié", "unverified")})</span>}</>, "agent-detail-email")}
        {item(tr("Téléphone", "Phone"), a.phone, "agent-detail-phone")}
        {item(tr("Responsable", "Manager"), a.manager?.name, "agent-detail-manager")}
        {item(tr("Créé le", "Created"), fmtDate(a.created_at, tr.lang), "agent-detail-created")}
        {item(tr("Dernière connexion", "Last login"), fmtDate(a.last_login, tr.lang), "agent-detail-last-login")}
        {item(tr("Dernière activité", "Last activity"), fmtDate(a.last_activity, tr.lang), "agent-detail-last-activity")}
      </div>
    </ACard>
  );
}

export default function AdminAgentDetail() {
  const { id } = useParams();
  const tr = useTr();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [managers, setManagers] = useState([]);
  const [ver, setVer] = useState(0);
  const load = useCallback(() => { api.get(`/admin/agents/${id}`).then((r) => { setA(r.data); setVer((v) => v + 1); }).catch(() => setA(false)); }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/admin/rbac/catalog").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/admin/agents", { params: { page_size: 100 } }).then((r) => setManagers(r.data.items)).catch(() => {});
  }, []);
  if (a === null) return <div className="py-20 text-center"><Loader2 className="mx-auto h-7 w-7 animate-spin text-accent" /></div>;
  if (a === false) return <div className="py-20 text-center text-slate-400">{tr("Agent introuvable", "Agent not found")}</div>;
  const tab = "rounded-full px-4 py-1.5 text-sm data-[state=active]:bg-accent data-[state=active]:text-white";
  return (
    <div data-testid="admin-agent-detail">
      <Link to="/admin/agents" className="mb-4 inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white" data-testid="agent-back"><ArrowLeft className="h-4 w-4" /> {tr("Tous les agents", "All agents")}</Link>
      <Summary a={a} tr={tr} />
      <Tabs defaultValue="profile">
        <TabsList className="mb-4 h-auto flex-wrap gap-1 bg-slate-900 p-1">
          <TabsTrigger value="profile" className={tab} data-testid="tab-profile">{tr("Profil", "Profile")}</TabsTrigger>
          <TabsTrigger value="permissions" className={tab} data-testid="tab-permissions">{tr("Permissions", "Permissions")}</TabsTrigger>
          <TabsTrigger value="languages" className={tab} data-testid="tab-languages">{tr("Langues support", "Support languages")}</TabsTrigger>
          <TabsTrigger value="activity" className={tab} data-testid="tab-activity">{tr("Activités", "Activity")}</TabsTrigger>
          <TabsTrigger value="performance" className={tab} data-testid="tab-performance">{tr("Performance du support", "Support performance")}</TabsTrigger>
          <TabsTrigger value="security" className={tab} data-testid="tab-security">{tr("Sécurité", "Security")}</TabsTrigger>
        </TabsList>
        <TabsContent value="profile"><ProfileTab key={`p${ver}`} a={a} managers={managers} reload={load} tr={tr} /></TabsContent>
        <TabsContent value="permissions"><PermsTab key={`pe${ver}`} a={a} catalog={catalog} canSensitive={user?.role === "super_admin"} reload={load} tr={tr} /></TabsContent>
        <TabsContent value="languages"><LangTab key={`l${ver}`} a={a} catalog={catalog} reload={load} tr={tr} /></TabsContent>
        <TabsContent value="activity"><AgentActivity agentId={a.id} /></TabsContent>
        <TabsContent value="performance"><SupportPerformance agentId={a.id} /></TabsContent>
        <TabsContent value="security"><SecurityTab a={a} reload={load} tr={tr} /></TabsContent>
      </Tabs>
    </div>
  );
}
